import React, { useState } from "react";
import { Button, theme } from "antd";
import type { GlobalToken } from "antd";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslate } from "@refinedev/core";
import {
  DotsThreeVerticalIcon,
  PencilSimpleLineIcon,
  TextAaIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { Dropdown } from "@libertify/ui";

interface PlaybookCardMenuProps {
  /** Names the trigger for screen readers — the card title next to it is
   *  visual only, so "options" alone would be ambiguous in a carousel. */
  playbookName: string;
  /** Opens the customize view in playbook-edit mode. */
  onEdit: () => void;
  onRename: () => void;
  onDelete: () => void;
}

/**
 * The ⋮ menu on a playbook card in the create-experience carousel: edit the
 * playbook's add-ons, rename it, or delete it.
 *
 * Rendered only for playbooks the current organization owns — that call belongs
 * to the carousel, which knows the owner; this component just draws the menu.
 */
export const PlaybookCardMenu: React.FC<PlaybookCardMenuProps> = ({
  playbookName,
  onEdit,
  onRename,
  onDelete,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  // Owned here rather than left to antd, so the trigger can report
  // `aria-expanded` — antd sets it on its own wrapper, not on our button.
  const [open, setOpen] = useState(false);

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayClassName={MENU_CLASS}
      items={[
        {
          key: "edit",
          label: t("createExperience.playbookMenu.edit"),
          icon: <PencilSimpleLineIcon size={16} weight="bold" />,
          onClick: onEdit,
        },
        {
          key: "rename",
          label: t("createExperience.playbookMenu.rename"),
          icon: <TextAaIcon size={16} weight="bold" />,
          onClick: onRename,
        },
        {
          key: "delete",
          label: t("createExperience.playbookMenu.delete"),
          icon: <TrashIcon size={16} weight="bold" />,
          danger: true,
          onClick: onDelete,
        },
      ]}
    >
      <KebabButton
        $token={token}
        type="text"
        size="small"
        aria-label={t("createExperience.playbookMenu.options", {
          name: playbookName,
        })}
        aria-haspopup="menu"
        aria-expanded={open}
        icon={<DotsThreeVerticalIcon size={18} weight="bold" />}
      />
    </Dropdown>
  );
};

// ─── Styled components ───

const MENU_CLASS = "playbook-card-menu";

/**
 * The design system's dropdown draws full-bleed rows on an unpadded menu; the
 * mock's is tighter and insets its rows, so the hover highlight reads as a
 * pill rather than a band edge to edge.
 *
 * A global style because the popup is portaled out of this tree — the same
 * reason `Dropdown` itself ships one. Scoped to this menu's own overlay class
 * so no other dropdown in the app is affected.
 *
 * Rendered by the carousel rather than by each menu: `createGlobalStyle` is
 * meant to be mounted once, and there is one of these per playbook card.
 */
export const PlaybookCardMenuStyles = createGlobalStyle`
  .${MENU_CLASS} .libertify-dropdown-menu.ant-dropdown-menu {
    padding: 4px;
    min-width: 178px;

    .ant-dropdown-menu-item {
      padding: 8px 10px;
      border-radius: 7px;
    }
  }
`;

const KebabButton = styled(Button)<{ $token: GlobalToken }>`
  /* Doubled selector to outweigh antd's own sizing: it injects its CSS-in-JS
     at runtime, so at equal specificity it lands after this rule and wins.
     min-width matters as much as width — antd sizes icon-only buttons with
     both, which is what left the button wider than it was tall. */
  && {
    width: 32px;
    min-width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 9px;
    background: rgb(9 14 25 / 25%);
    backdrop-filter: blur(4px);
    color: ${({ $token }) => $token.colorWhite};

    &:hover {
      background: rgb(9 14 25 / 45%);
      color: ${({ $token }) => $token.colorWhite};
    }

    &:focus-visible {
      outline: 2px solid ${({ $token }) => $token.colorPrimary};
      outline-offset: 2px;
    }
  }
`;
